/**
 * WorldGlobe — purely decorative wireframe globe (CSS/SVG only, no canvas/library).
 *
 * Renders a dot-grid sphere with blue/violet glow rings. It is positioned
 * absolutely and is non-interactive (pointer-events: none) so it never
 * interferes with functional UI.
 *
 * Props:
 *   size      — pixel diameter (default 320)
 *   className — extra positioning classes for the wrapper
 *   style     — extra inline styles for the wrapper
 */
const WorldGlobe = ({ size = 320, className = '', style = {} }) => {
  // Build a dot grid that fills the sphere (latitude/longitude scatter).
  const dots = [];
  const cx = 100;
  const cy = 100;
  const r = 92;
  for (let lat = -80; lat <= 80; lat += 12) {
    const latRad = (lat * Math.PI) / 180;
    const ringR = r * Math.cos(latRad);
    const y = cy + r * Math.sin(latRad);
    const count = Math.max(6, Math.round(ringR / 5));
    for (let i = 0; i < count; i++) {
      const ang = (i / count) * Math.PI * 2;
      const x = cx + ringR * Math.cos(ang);
      // Fake depth: dots toward the back render fainter/smaller.
      const depth = (Math.sin(ang) + 1) / 2; // 0 (back) .. 1 (front)
      dots.push(
        <circle
          key={`${lat}-${i}`}
          cx={x}
          cy={y}
          r={0.5 + depth * 0.9}
          fill="#7c9cff"
          opacity={0.18 + depth * 0.55}
        />
      );
    }
  }

  return (
    <div
      className={`world-globe-wrap ${className}`}
      style={{ width: size, height: size, ...style }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 200 200"
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="wg-core" cx="38%" cy="32%" r="75%">
            <stop offset="0%" stopColor="#1e3a8a" stopOpacity="0.55" />
            <stop offset="55%" stopColor="#0f1640" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#080d1f" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="wg-glow" cx="50%" cy="50%" r="50%">
            <stop offset="60%" stopColor="#3b82f6" stopOpacity="0" />
            <stop offset="88%" stopColor="#6366f1" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Outer glow halo */}
        <circle cx="100" cy="100" r="99" fill="url(#wg-glow)" className="world-globe-pulse" />

        {/* Orbit rings */}
        <g
          fill="none"
          stroke="#6366f1"
          strokeOpacity="0.35"
          className="world-globe-spin"
          style={{ transformOrigin: '100px 100px' }}
        >
          <ellipse cx="100" cy="100" rx="99" ry="38" />
          <ellipse cx="100" cy="100" rx="92" ry="70" stroke="#3b82f6" strokeOpacity="0.22" />
        </g>

        {/* Sphere core wash */}
        <circle cx="100" cy="100" r="92" fill="url(#wg-core)" />

        {/* Dot-grid surface */}
        <g>{dots}</g>

        {/* Rim highlight */}
        <circle
          cx="100"
          cy="100"
          r="92"
          fill="none"
          stroke="#3b82f6"
          strokeOpacity="0.45"
          strokeWidth="0.75"
        />
      </svg>
    </div>
  );
};

export default WorldGlobe;
